import { C } from '../../../../lib/design';
import { Ic, ICON_PATHS } from '../../../../lib/ui';
import { useI18n } from '../../../../i18n';
import type { FichaDocumento } from '../../../shared/services/api';
import { Section, EmptyState, DocLink, InlineEdit, AddItemRow } from './ProcessSheetHelpers';
import type { HerramientaItem, FichaEditHook } from './ProcessSheetHelpers';

interface ProcessSheetToolsProps {
  herramientas: FichaEditHook<HerramientaItem>;
  toolDocs: FichaDocumento[];
  navigate: (p: string) => void;
  editMode: boolean;
  addingSection: string | null;
  setAddingSection: (v: string | null) => void;
  newItemValue: string;
  setNewItemValue: (v: string) => void;
  onAdd: (
    section: string,
    hook: { crear: (d: Record<string, unknown>) => Promise<unknown> },
  ) => void;
}

export function ProcessSheetTools({
  herramientas,
  toolDocs,
  navigate,
  editMode,
  addingSection,
  setAddingSection,
  newItemValue,
  setNewItemValue,
  onAdd,
}: ProcessSheetToolsProps) {
  const { t } = useI18n();

  return (
    <Section icon={<Ic d={ICON_PATHS.tool} s={16} />} title={`6. ${t.processSheet.sections.tools}`}>
      {herramientas.items.length === 0 && !herramientas.loading ? (
        toolDocs.length > 0 ? (
          toolDocs.map((d) => <DocLink key={d.id} doc={d} navigate={navigate} />)
        ) : (
          <EmptyState message={t.processSheet.noData} />
        )
      ) : (
        herramientas.items.map((item) => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 0',
              borderBottom: `1px solid ${C.borderAlt}`,
              flexWrap: 'wrap',
            }}
          >
            <Ic d={ICON_PATHS.tool} s={13} c={C.azul2} />
            {editMode ? (
              <InlineEdit
                value={item.codigo ?? ''}
                onSave={(v) => herramientas.actualizar(item.id, { codigo: v || null })}
                editMode={editMode}
                placeholder={t.processSheet.placeholders.codigo}
                style={{
                  fontSize: 11,
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: C.azul2,
                  flex: 'unset',
                  width: 80,
                }}
              />
            ) : item.codigo ? (
              <span
                style={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  fontWeight: 700,
                  color: C.azul2,
                }}
              >
                {item.codigo}
              </span>
            ) : null}
            <InlineEdit
              value={item.nombre}
              onSave={(v) => herramientas.actualizar(item.id, { nombre: v })}
              editMode={editMode}
            />
            {!editMode && item.moduloVianet && (
              <span
                style={{
                  fontSize: 10,
                  color: C.textMut,
                  padding: '1px 6px',
                  borderRadius: 4,
                  background: `${C.celeste}15`,
                }}
              >
                {item.moduloVianet}
              </span>
            )}
            {editMode && (
              <button
                onClick={() => herramientas.eliminar(item.id)}
                style={{
                  border: 'none',
                  background: 'none',
                  color: '#EF4444',
                  cursor: 'pointer',
                  fontSize: 14,
                  padding: 2,
                }}
              >
                x
              </button>
            )}
            {editMode && (
              <div style={{ display: 'flex', gap: 6, width: '100%', marginTop: 4 }}>
                <InlineEdit
                  value={item.descripcion ?? ''}
                  onSave={(v) => herramientas.actualizar(item.id, { descripcion: v || null })}
                  editMode={editMode}
                  placeholder={t.processSheet.placeholders.descripcion}
                  style={{ fontSize: 11, color: C.textMut, flex: 1 }}
                />
                <InlineEdit
                  value={item.moduloVianet ?? ''}
                  onSave={(v) => herramientas.actualizar(item.id, { moduloVianet: v || null })}
                  editMode={editMode}
                  placeholder={t.processSheet.placeholders.modulo}
                  style={{ fontSize: 11, color: C.textMut, flex: 'unset', width: 120 }}
                />
              </div>
            )}
          </div>
        ))
      )}
      {editMode && (
        <AddItemRow
          sectionKey="herramientas"
          addingSection={addingSection}
          setAddingSection={setAddingSection}
          newItemValue={newItemValue}
          setNewItemValue={setNewItemValue}
          onAdd={() => onAdd('herramientas', herramientas)}
          addLabel={t.processSheet.addItem}
        />
      )}
    </Section>
  );
}
